import {
  WSMenuItem,
  copyText,
  useWSModal,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import {
  Currency,
  InvoiceRow,
  InvoiceStatus,
  PayeeReviewStatus
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useModalRefund } from "../../../../Invoices/components/ModalRefund";
import { useModalMarkInvoiceDeposited } from "../../../../Invoices/components/ModalMarkInvoiceDeposited";
import { queriesInternalAccounts, queriesInvoices } from "@query";
import { useSession } from "@hooks/useSession";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import {
  useMutationAcceptInvoice,
  useMutationDownloadInvoicePdf,
  useMutationDownloadInvoiceReceiptPdf,
  useMutationRetryInvoicePayment,
  useMutationSendInvoice
} from "../../../../query/payments/mutations";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { useQueryMemberProfile } from "../../../../query/users/queries";
import { useSetWSStore } from "../../../../store";
import { ModalDelete } from "./ModalDelete";
import { ModalDispute } from "./ModalDispute";
import { selectorIsInstantPayoutAllowed } from "./selectorIsInstantPayoutAllowed";
import { selectorIsInvoiceDeposited } from "./selectorIsInvoiceDeposited";

/**
 * Row-menu actions for an invoice. Migrated to the v3 search `InvoiceRow`:
 * gating now reads the raw `status` (+ `payeeReviewStatus` / `recurringInvoiceId`)
 * instead of the v1 derived `oldStatus` / `member.workflowStatus` /
 * `invoiceTemplateId`.
 *
 * Edit-access is the row's account matching the session account
 * (`invoice.accountId === session.accountId`); accept/dispute are gated on
 * `payeeReviewStatus`. The refund gate no longer pre-checks the refundable
 * amount (no `amountDetails` on the row) — the refund modal fetches the full
 * invoice and caps the amount.
 */
export const useInvoiceActions = () => {
  const userId = useUserId();
  const { accountId } = useSession();
  const history = useHistory();
  const location = useLocation();
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const queryMember = useQueryMemberProfile(userId);
  const { openSnackbar } = useWSSnackbar();
  const [sendInvoice] = useMutationSendInvoice();
  const [createInvoiceSecureLink] =
    queriesInvoices.createInvoiceSecureLink.useMutation();
  const [sendInvoiceReminder] = queriesInvoices.remindInvoice.useMutation({
    onSuccess: () => {
      openSnackbar({ type: "success", message: "Reminder sent" });
    }
  });
  const [downloadInvoicePdf] = useMutationDownloadInvoicePdf();
  const [downloadInvoiceReceiptPdf] = useMutationDownloadInvoiceReceiptPdf();
  const [acceptInvoice] = useMutationAcceptInvoice();
  const [retryPayment] = useMutationRetryInvoicePayment();
  const featureFlagsQuery = useFeatureFlags();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const setStore = useSetWSStore();
  const modalRefund = useModalRefund();
  const modalMarkDeposited = useModalMarkInvoiceDeposited();
  const modalDelete = useWSModal(ModalDelete, { size: "S" });
  const modalDispute = useWSModal(ModalDispute, { size: "S" });

  return useCallback(
    (invoice: InvoiceRow) => {
      const menuItems: WSMenuItem[] = [];
      const invoiceId = invoice.id;
      const status = invoice.status;
      // Edit-access = the invoice belongs to the current session account.
      const canEdit = !!accountId && invoice.accountId === accountId;

      // A search row always carries an id; bail rather than emit actions that
      // would fire a mutation with a missing/empty id.
      if (!invoiceId) {
        return menuItems;
      }

      // Edit
      if (
        canEdit &&
        [
          InvoiceStatus.CREATED,
          InvoiceStatus.PENDING,
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE
        ].includes(status as InvoiceStatus)
      ) {
        menuItems.push({
          icon: "edit",
          label: "Edit",
          onClick: () => {
            setStore({ invoiceFormBackPath: location.pathname });
            history.push(`/member/invoices/${invoiceId}/edit`);
          }
        });
      }

      // Duplicate
      menuItems.push({
        icon: "plus-circle",
        label: "Duplicate",
        onClick: () => {
          history.push(`/member/invoices/${invoiceId}/duplicate`);
        }
      });

      // Delete
      if (canEdit && status === InvoiceStatus.CREATED) {
        menuItems.push({
          icon: "exit",
          label: "Remove",
          onClick: () => {
            modalDelete.open({ invoice });
          }
        });
      }

      // Cancel
      if (
        canEdit &&
        [
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE,
          InvoiceStatus.PENDING
        ].includes(status as InvoiceStatus)
      ) {
        menuItems.push({
          icon: "exit",
          label: "Cancel invoice",
          onClick: () => {
            modalDelete.open({ invoice });
          }
        });
      }

      // Send
      if (canEdit && status === InvoiceStatus.CREATED) {
        menuItems.push({
          icon: "send",
          label: "Send now",
          onClick: () => {
            sendInvoice({ invoiceId });
          }
        });
      }

      // Mark deposited
      if (
        [
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE,
          InvoiceStatus.PAYMENT_IN_TRANSIT
        ].includes(status as InvoiceStatus)
      ) {
        menuItems.push({
          icon: "dollar-circle",
          label: "Mark deposited",
          onClick: () => {
            modalMarkDeposited.open({
              invoiceId,
              amount: invoice.amount ?? 0,
              currency: invoice.currency ?? Currency.USD,
              invoiceNumber: invoice.invoiceNumber
            });
          }
        });
      }

      // Download PDF
      if (
        status !== InvoiceStatus.CREATED &&
        status !== InvoiceStatus.PENDING
      ) {
        menuItems.push({
          icon: "download",
          label: "Download PDF",
          onClick: () => {
            downloadInvoicePdf({ invoiceId });
          }
        });
      }

      // Download Receipt PDF
      if (
        status === InvoiceStatus.PAID ||
        status === InvoiceStatus.PAID_OFF_PLATFORM
      ) {
        menuItems.push({
          icon: "download",
          label: "Download receipt PDF",
          onClick: () => {
            downloadInvoiceReceiptPdf({ invoiceId });
          }
        });
      }

      // Send reminder
      if (
        [InvoiceStatus.OPENED, InvoiceStatus.OVERDUE].includes(
          status as InvoiceStatus
        )
      ) {
        menuItems.push({
          icon: "calendar",
          label: "Send reminder",
          onClick: () => {
            sendInvoiceReminder({ invoiceId });
          }
        });
      }

      // Copy link
      if (
        [
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE,
          InvoiceStatus.PAID,
          InvoiceStatus.PAYMENT_IN_TRANSIT
        ].includes(status as InvoiceStatus)
      ) {
        menuItems.push({
          icon: "download",
          label: "Copy invoice link",
          onClick: async () => {
            const link = await createInvoiceSecureLink({ invoiceId });
            if (link?.url) {
              copyText(link.url);
            }
          }
        });
      }

      // Accept
      if (
        [
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE,
          InvoiceStatus.PENDING
        ].includes(status as InvoiceStatus) &&
        invoice.payeeReviewStatus !== PayeeReviewStatus.ACCEPTED
      ) {
        menuItems.push({
          icon: "check",
          label: "Accept invoice",
          onClick: () => {
            acceptInvoice({ invoiceId });
          }
        });
      }

      // Dispute
      if (
        [
          InvoiceStatus.OPENED,
          InvoiceStatus.OVERDUE,
          InvoiceStatus.PENDING
        ].includes(status as InvoiceStatus) &&
        invoice.payeeReviewStatus !== PayeeReviewStatus.DISPUTED
      ) {
        menuItems.push({
          icon: "alert-circle",
          label: "Dispute invoice",
          onClick: () => {
            modalDispute.open({ invoice });
          }
        });
      }

      // Refund
      if (
        featureFlagsQuery.data?.refundButton &&
        selectorIsInvoiceDeposited(invoice) &&
        queryInternalAccounts.data?.data?.some(
          account => account.type === "Clearing"
        )
      ) {
        menuItems.push({
          icon: "dollar-circle",
          label: "Refund",
          onClick: () => {
            modalRefund.open({ invoice });
          }
        });
      }

      // Retry payment
      if (
        [InvoiceStatus.OPENED, InvoiceStatus.OVERDUE].includes(
          status as InvoiceStatus
        ) &&
        !!invoice.recurringInvoiceId
      ) {
        menuItems.push({
          icon: "refresh-h",
          label: "Retry payment",
          onClick: () => {
            retryPayment({ invoiceId });
          }
        });
      }

      // Instant payout
      if (
        selectorIsInstantPayoutAllowed(
          invoice,
          queryPayoutSettings.data,
          queryMember.data
        )
      ) {
        menuItems.push({
          icon: "instant-circle",
          label: "Request instant payout",
          onClick: () => {
            history.push(`${location.pathname}/${invoiceId}/instant-deposit`);
          }
        });
      }

      return menuItems;
    },
    [
      accountId,
      acceptInvoice,
      downloadInvoicePdf,
      downloadInvoiceReceiptPdf,
      featureFlagsQuery.data?.refundButton,
      queryInternalAccounts.data,
      history,
      location.pathname,
      createInvoiceSecureLink,
      modalMarkDeposited,
      modalDelete,
      modalDispute,
      modalRefund,
      queryMember.data,
      queryPayoutSettings.data,
      retryPayment,
      sendInvoice,
      sendInvoiceReminder,
      setStore
    ]
  );
};
